import React, { Fragment, useState } from 'react';
import type { FC } from 'react';
import { join, dirname, relative } from 'node:path';
import { match } from 'variant';
import { useAsync, Spinner, Alert, Text, Box } from '@wix/cli-ui-kit';
import { outputFile } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { SupportedExtensionTopologyItem } from '@wix/cli-astro-definitions';
import { getEditorReactComponentExtensionsDir } from '@wix/cli-astro-definitions';
import kebabCase from 'lodash/kebabCase.js';
import { extractComponentManifest } from '@wix/zero-config-implementation';
import type { ExtractionError } from '@wix/zero-config-implementation';
import { useProjectModel } from '../../model.js';
import { assertBuildOutputExists } from '../../files/assert-build-output-exists.js';
import { readBuildMetadata } from '../../files/build-metadata.js';

type EditorReactExtension = Extract<
  SupportedExtensionTopologyItem,
  { type: 'EDITOR_REACT_COMPONENT' }
>;

type TaskStatus =
  | { status: 'running'; componentId: string }
  | { status: 'success'; relativePath: string }
  | { status: 'warning'; relativePath: string; errors: ExtractionError[] };

type CompletedTask = Extract<TaskStatus, { status: 'success' | 'warning' }>;

async function generateManifestForExtension(
  ext: EditorReactExtension,
  projectFolder: string
): Promise<{ relativePath: string; errors: ExtractionError[] }> {
  try {
    const { srcPath, distPath } = ext.topology['resources.client.componentUrl'];
    const folder = dirname(srcPath);
    const route = relative(
      getEditorReactComponentExtensionsDir(projectFolder),
      folder
    );
    const generatedFileName = kebabCase(route);
    const filePath = join(folder, `${generatedFileName}.generated.ts`);

    const res = await extractComponentManifest(srcPath, distPath);
    const content = [
      `/**`,
      ` * THIS FILE IS AUTO-GENERATED`,
      ` * Changes to this file will be overwritten during the next build/sync.`,
      ` * To make changes, update the source code or use the ${generatedFileName}.extension.ts file`,
      ` */`,
      ``,
      `import type { EditorElement } from '@wix/react-component-schema';`,
      ``,
      `export const editorElement = ${JSON.stringify(res.component.editorElement, null, 2)} as EditorElement;`,
      ``,
    ].join('\n');

    await outputFile(filePath, content);

    return {
      relativePath: relative(projectFolder, filePath),
      errors: res.errors,
    };
  } catch (cause) {
    throw new CliError({
      code: CliErrorCode.FailedToGenerateExtensionConfig({
        extensionId: ext.id,
      }),
      cause,
    });
  }
}

function formatExtractionError(err: ExtractionError): string {
  return `[${err.phase}] ${err.componentName}: ${err.error.message}`;
}

const ManifestTaskList: FC<{
  extensions: EditorReactExtension[];
  projectFolder: string;
}> = ({ extensions, projectFolder }) => {
  const total = extensions.length;
  const [tasks, setTasks] = useState<TaskStatus[]>([]);

  useAsync(async () => {
    const completed: CompletedTask[] = [];
    for (const ext of extensions) {
      setTasks([...completed, { status: 'running', componentId: ext.id }]);
      const { relativePath, errors } = await generateManifestForExtension(
        ext,
        projectFolder
      );
      completed.push(
        errors.length === 0
          ? { status: 'success', relativePath }
          : { status: 'warning', relativePath, errors }
      );
    }
    setTasks(completed);
  }, []);

  return (
    <>
      {tasks.map((task, i) => {
        const stepper = `[${i + 1}/${total}]`;
        switch (task.status) {
          case 'running':
            return (
              <Spinner
                key={i}
                text={`${stepper} Generating config for component ${task.componentId}`}
              />
            );
          case 'success':
            return (
              <Alert key={i} type="success">
                {`${stepper} Generated ${task.relativePath}`}
              </Alert>
            );
          case 'warning':
            return (
              <Fragment key={i}>
                <Alert type="warning">
                  {`${stepper} Generated ${task.relativePath}`}
                </Alert>
                {task.errors.map((err, j) => (
                  <Box
                    key={j}
                    paddingLeft={2}
                    paddingBottom={1}
                    flexDirection="column"
                  >
                    <Alert type="insight">{formatExtractionError(err)}</Alert>
                  </Box>
                ))}
              </Fragment>
            );
        }
      })}
    </>
  );
};

export const GenerateManifestCommand: FC = () => {
  const { model } = useProjectModel();

  const { status } = useAsync(async () => {
    await assertBuildOutputExists(model.projectFolder, model.config);
    return readBuildMetadata(model.projectFolder, model.config);
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => null,
    Success: ({ result }) => {
      const extensions = (result.extensionsTopology ?? []).filter(
        (ext): ext is EditorReactExtension =>
          ext.type === 'EDITOR_REACT_COMPONENT'
      );
      if (extensions.length === 0) {
        return <Text>No extensions that can generate manifest for exist</Text>;
      }

      return (
        <ManifestTaskList
          extensions={extensions}
          projectFolder={model.projectFolder}
        />
      );
    },
  });
};
